import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter, Button, Text, Box, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkUpdateTracks, fetchLicenses } from '../../../api/libraryApi';
import { LicenseSelect } from '../../library/components/LicenseSelect';

interface PlaylistLicenseSuggestDialogProps {
  open: boolean;
  playlistId: string;
  /** Content ids of every track in the playlist. */
  trackIds: string[];
  /** The override value just set (true/false/null) — tailors the copy + default license. */
  overrideValue: boolean | null;
  onClose: () => void;
}

/**
 * Suggested follow-up after an admin changes a playlist's licensed override: optionally apply a
 * single license to every track in the playlist. Pre-selects the Streamko system license when the
 * playlist was forced "Licensed"; otherwise starts empty (clearing the license is a valid choice).
 */
export function PlaylistLicenseSuggestDialog({
  open, playlistId, trackIds, overrideValue, onClose,
}: PlaylistLicenseSuggestDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [selected, setSelected] = useState<{ id: string | null; name: string | null }>({ id: null, name: null });

  const { data: licenses } = useQuery({ queryKey: ['admin-licenses'], queryFn: fetchLicenses, enabled: open });

  // Default: forcing "Licensed" pre-selects the system (Streamko) license so tracks truly match.
  useEffect(() => {
    if (!open) return;
    if (overrideValue === true && licenses) {
      const system = licenses.find((l) => l.isSystem);
      setSelected(system ? { id: system.id, name: system.name } : { id: null, name: null });
    } else {
      setSelected({ id: null, name: null });
    }
  }, [open, overrideValue, licenses]);

  const apply = useMutation({
    mutationFn: () => bulkUpdateTracks(trackIds, ['licenseId'], { licenseId: selected.id }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', playlistId] });
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
      toast({
        title: t('admin.playlists.licenseSuggest.done', { count: res.updatedCount }),
        status: 'success', duration: 3000, isClosable: true,
      });
      onClose();
    },
    onError: () => toast({
      title: t('admin.playlists.licenseSuggest.failed'), status: 'error', duration: 4000, isClosable: true,
    }),
  });

  const stateLabel = overrideValue === true ? t('admin.playlists.licensed.licensed')
    : overrideValue === false ? t('admin.playlists.licensed.unlicensed')
      : t('admin.playlists.licensed.auto');

  return (
    <AlertDialog isOpen={open} leastDestructiveRef={cancelRef} onClose={apply.isPending ? () => {} : onClose} isCentered>
      <AlertDialogOverlay />
      <AlertDialogContent data-testid="playlist-license-suggest-dialog" borderRadius="16px" maxW="480px">
        <AlertDialogHeader>{t('admin.playlists.licenseSuggest.title')}</AlertDialogHeader>
        <AlertDialogBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            {t('admin.playlists.licenseSuggest.body', { count: trackIds.length, state: stateLabel })}
          </Text>
          <Box>
            <LicenseSelect
              value={selected.name}
              onChange={(id, name) => setSelected({ id, name })}
              label={t('admin.playlists.licenseSuggest.licenseLabel')}
              inputTestId="playlist-license-suggest-select"
              isDisabled={apply.isPending}
            />
          </Box>
        </AlertDialogBody>
        <AlertDialogFooter gap={2}>
          <Button ref={cancelRef} variant="outline" onClick={onClose} isDisabled={apply.isPending} data-testid="playlist-license-suggest-skip">
            {t('admin.playlists.licenseSuggest.skip')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={() => apply.mutate()}
            isLoading={apply.isPending}
            isDisabled={!selected.id}
            data-testid="playlist-license-suggest-apply"
          >
            {t('admin.playlists.licenseSuggest.apply', { count: trackIds.length })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default PlaylistLicenseSuggestDialog;
